import React, { useState } from "react";
import { X, Mail, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * iter71c · Universal temporary-login-code dialog.
 *
 * Two steps:
 *   1. User types their email → we call /auth/temp-login-code/request.
 *      Server always 200s (enumeration-safe) and — if the email is on
 *      file — emails an 8-digit code.
 *   2. User types the code → we call /auth/temp-login-code/verify.
 *      On success the server sets session cookies and we call
 *      `onSuccess(user, superAdmin)` so the parent can navigate.
 */
export default function TempCodeDialog({ open, onClose, onSuccess, roleLabel }) {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setStep("request"); setEmail(""); setCode(""); setErr(""); setBusy(false);
  };

  const closeAndReset = () => { reset(); onClose?.(); };

  const requestCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr("Email is required."); return; }
    setBusy(true);
    try {
      await api.post("/auth/temp-login-code/request", { email: email.trim() });
      setStep("verify");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!code.trim()) { setErr("Enter the 8-digit code from your email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/temp-login-code/verify", {
        email: email.trim(), code: code.trim(),
      });
      onSuccess?.(data.user, !!data.super_admin);
      reset();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
         onClick={closeAndReset} data-testid="temp-code-dialog">
      <div className="w-full max-w-md bg-cream border border-[#D9C68A]/40 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#E5E3D8] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-[#B59A5A]" />
            <span className="overline">Temporary Login Code</span>
          </div>
          <button onClick={closeAndReset} data-testid="temp-code-close"
                  className="text-[#4A4A4A] hover:text-[#1A1A1A]"><X size={18} /></button>
        </div>

        <div className="p-6">
          {step === "request" && (
            <form onSubmit={requestCode} className="space-y-4" data-testid="temp-code-step-request">
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                We&rsquo;ll email an 8-digit login code good for 30 minutes. Use it once to sign in
                {roleLabel ? ` to the ${roleLabel}` : ""} — no password required.
              </p>
              <div>
                <label className="label-elegant block mb-1" htmlFor="tc-email">Email address</label>
                <input
                  id="tc-email"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="temp-code-email"
                  className="input-field w-full"
                  placeholder="you@example.com"
                />
              </div>
              {err && (
                <div className="border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm flex items-start gap-2"
                     data-testid="temp-code-request-error">
                  <AlertTriangle size={14} className="mt-0.5" />{err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy || !email}
                data-testid="temp-code-request-submit"
                className="w-full bg-[#1A422D] text-[#F9F8F4] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send Temporary Code"}
              </button>
            </form>
          )}

          {step === "verify" && (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="temp-code-step-verify">
              <div className="flex items-start gap-2 border border-[#1A422D] bg-[#EFF3EC] px-3 py-2 text-[#1A422D] text-sm"
                   data-testid="temp-code-sent">
                <CheckCircle2 size={14} className="mt-0.5" />
                If <strong>{email}</strong> is on file, a code has been sent. Check your inbox (and spam folder).
              </div>
              <div>
                <label className="label-elegant block mb-1" htmlFor="tc-code">8-digit code</label>
                <input
                  id="tc-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  data-testid="temp-code-code"
                  className="input-field w-full text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="••••••••"
                />
              </div>
              {err && (
                <div className="border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm flex items-start gap-2"
                     data-testid="temp-code-verify-error">
                  <AlertTriangle size={14} className="mt-0.5" />{err}
                </div>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={busy || code.length !== 8}
                  data-testid="temp-code-verify-submit"
                  className="flex-1 bg-[#1A422D] text-[#F9F8F4] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
                >
                  {busy ? "Verifying…" : "Sign In"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("request"); setCode(""); setErr(""); }}
                  data-testid="temp-code-back"
                  className="text-xs uppercase tracking-[0.22em] text-[#4A4A4A]"
                >
                  Wrong email?
                </button>
              </div>
              <p className="text-[11px] text-[#7A7A7A]">
                Code expires in 30 minutes. Didn&rsquo;t receive it? Check spam, or ask your
                account owner to update your email address.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
